<template>
  <div class="address-formfields column">
    <input-text
      v-if="includeCompany"
      :value="company"
      :label="
        companyRequired ? $t('labels.company') : $t('labels.companyOptional')
      "
      :error="fieldErrors?.company"
      :required="companyRequired"
      :hide-errors="!showFormErrors"
      autocomplete="organization"
      @update:value="$emit('update:company', $event)"
    />
    <input-text
      v-if="includeCompany"
      :value="vatId"
      :label="$t('labels.vatId')"
      :error="fieldErrors?.vatId"
      :required="companyRequired"
      :hide-errors="!showFormErrors"
      autocomplete="vat-id"
      @update:value="$emit('update:vatId', $event)"
    />
    
    <div class="input__block">
      <input-text
        :value="firstname"
        :label="$t('labels.firstname')"
        :required="required"
        :error="fieldErrors?.firstname"
        :hide-errors="!showFormErrors"
        autocomplete="given-name"
        @update:value="$emit('update:firstname', $event)"
      />
      <input-text
        :value="lastname"
        :label="$t('labels.lastname')"
        :required="required"
        :error="fieldErrors?.lastname"
        :hide-errors="!showFormErrors"
        autocomplete="family-name"
        @update:value="$emit('update:lastname', $event)"
      />
    </div>

    <input-text
      :value="street"
      :label="$t('labels.streetAndNumber')"
      :error="fieldErrors?.street"
      :required="required"
      :hide-errors="!showFormErrors"
      @update:value="$emit('update:street', $event)"
    />

    <input-text
      :value="supplement"
      :label="$t('labels.supplement')"
      :error="fieldErrors?.supplement"
      :required="false"
      :hide-errors="!showFormErrors"
      @update:value="$emit('update:supplement', $event)"
    />

    <div class="input__block address-formfields__cityline">
      <input-text
        :value="zipcode"
        :label="$t('labels.zipCode')"
        :error="fieldErrors?.zipcode"
        :required="required"
        :hide-errors="!showFormErrors"
        autocomplete="postal-code"
        @update:value="$emit('update:zipcode', $event)"
      />

      <input-text
        :value="city"
        :label="$t('labels.city')"
        :error="fieldErrors?.city"
        :required="required"
        :hide-errors="!showFormErrors"
        autocomplete="address-level2"
        @update:value="$emit('update:city', $event)"
      />
    </div>

    <input-text
      v-model:value="countryName"
      :label="$t('labels.country')"
      :error="fieldErrors?.country"
      :required="required"
      :hide-errors="!showFormErrors"
      :autocomplete-options="countriesAutoCompleteList"
      :requires-option="true"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useCountries } from "@/helpers/countries";

import InputText from "@core/input/InputText.vue";

const props = withDefaults(
  defineProps<{
    firstname?: string;
    lastname?: string;
    company?: string;
    vatId?: string;
    street?: string;
    supplement?: string;
    zipcode?: string;
    city?: string;
    country?: string;

    showFormErrors?: boolean;
    fieldErrors?: Record<string, string>;
    required?: boolean;
    includeCompany?: boolean;
    companyRequired?: boolean;
  }>(),
  {
    firstname: "",
    lastname: "",
    company: "",
    vatId: "",
    street: "",
    supplement: "",
    zipcode: "",
    city: "",
    country: "",

    showFormErrors: true,
    fieldErrors: () => ({}),
    required: true,
    includeCompany: true,
    companyRequired: true
  }
);

const emit = defineEmits<{
  (e: "update:firstname", value: string): void;
  (e: "update:lastname", value: string): void;
  (e: "update:company", value: string): void;
  (e: "update:vatId", value: string): void;
  (e: "update:street", value: string): void;
  (e: "update:supplement", value: string): void;
  (e: "update:zipcode", value: string): void;
  (e: "update:city", value: string): void;
  (e: "update:country", value: string): void;
}>();

const { countriesAutoCompleteList, codeToName, nameToCode } = useCountries();

const countryName = ref(codeToName(props.country));

watch(() => props.country, (value) => {
  const name = codeToName(value);
  if (name != countryName.value) {
    countryName.value = name;
  }
});

watch(() => countryName.value, (value) => {
  const countryCode = nameToCode(value);
  if (countryCode && countryCode != props.country) {
    emit("update:country", countryCode);
  }
});
</script>

<style lang="scss">
.address-formfields {
  &__streetline {
    display: grid;
    grid-template-columns: 1fr minmax(min-content, 5rem);
  }
  &__cityline {
    display: grid;
    grid-template-columns: minmax(min-content, 5rem) 1fr;
  }
}
</style>
